import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Switch,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../utils/axiosInstance";
import { useAppAuth } from "../context/AuthContext";
import ProtectedRoute from "../components/ProtectedRoute";
import ShopInfo from "./ShopInfo";
import { useTheme } from "@mui/material/styles";

const ShopsPage = () => {
  const { appUser } = useAppAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const res = await api.get("/shops");
      setShops(res.data);
    } catch (err) {
      console.error("Failed to fetch shops:", err);
      setToast({ open: true, message: "Failed to fetch shops", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (shop) => {
    setSelectedShop(shop);
    setFormOpen(true);
  };

  const handleDelete = async (shop) => {
    if (!window.confirm(`Delete shop "${shop.name}"?`)) return;
    try {
      await api.delete(`/shops/${shop.id}`);
      setToast({ open: true, message: "Shop deleted successfully", severity: "success" });
      fetchShops();
    } catch (err) {
      console.error("Failed to delete shop:", err);
      setToast({ open: true, message: "Error deleting shop", severity: "error" });
    }
  };

  const toggleField = async (shop, field) => {
    const updatedShops = shops.map((s) =>
      s.id === shop.id ? { ...s, [field]: !s[field] } : s
    );
    setShops(updatedShops);

    try {
      await api.put(`/shops/${shop.id}`, { [field]: !shop[field] });
      setToast({
        open: true,
        message: `${field.charAt(0).toUpperCase() + field.slice(1)} ${
          !shop[field] ? "enabled" : "disabled"
        } for ${shop.name}`,
        severity: "success",
      });
    } catch (err) {
      setShops(shops); // revert if failed
      console.error(`Toggle ${field} failed:`, err);
      setToast({ open: true, message: `Failed to update ${field}`, severity: "error" });
    }
  };

  const handleFormSaved = () => {
    setSelectedShop(null);
    setFormOpen(false);
    fetchShops();
  };

  const filteredShops = shops.filter(
    (shop) =>
      shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (shop.category && shop.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ProtectedRoute allowedRoles={["super_admin"]}>
      <Box p={3}>
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "stretch" : "center"}
          mb={3}
          gap={2}
        >
          <Typography variant="h5">Manage Shops</Typography>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
            <TextField
              size="small"
              placeholder="Search by name or category"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth={isMobile}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
            >
              Add Shop
            </Button>
          </Box>
        </Box>

        {isMobile ? (
          <Stack spacing={2}>
            {filteredShops.map((shop) => (
              <Card key={shop.id} variant="outlined">
                <CardContent>
                  <Typography variant="h6">{shop.name}</Typography>
                  <Typography variant="body2">Owner: {shop.owner_email || "—"}</Typography>
                  <Typography variant="body2">Category: {shop.category}</Typography>
                  <Typography variant="body2">Location: {shop.location}</Typography>
                  <Typography variant="body2">Contact: {shop.contact}</Typography>
                  <Typography variant="body2">Created: {new Date(shop.created_at).toLocaleDateString()}</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    <Switch checked={!!shop.is_premium} onChange={() => toggleField(shop, "is_premium")} color="warning" />
                    <Typography variant="caption">Premium</Typography>
                    <Switch checked={!!shop.verified} onChange={() => toggleField(shop, "verified")} color="primary" />
                    <Typography variant="caption">Verified</Typography>
                    <Switch checked={!!shop.active} onChange={() => toggleField(shop, "active")} color="success" />
                    <Typography variant="caption">Active</Typography>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <IconButton onClick={() => handleEdit(shop)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(shop)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Owner Email</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredShops.map((shop) => (
                  <TableRow key={shop.id} sx={{ backgroundColor: shop.is_premium ? "rgba(255, 223, 186, 0.4)" : "inherit" }}>
                    <TableCell>{shop.name}</TableCell>
                    <TableCell>{shop.owner_email || "—"}</TableCell>
                    <TableCell>{shop.category}</TableCell>
                    <TableCell>{shop.location}</TableCell>
                    <TableCell>{shop.contact}</TableCell>
                    <TableCell>{new Date(shop.created_at).toLocaleDateString()}</TableCell>
                    <TableCell><Switch checked={!!shop.is_premium} onChange={() => toggleField(shop, "is_premium")} color="warning" /></TableCell>
                    <TableCell><Switch checked={!!shop.verified} onChange={() => toggleField(shop, "verified")} color="primary" /></TableCell>
                    <TableCell><Switch checked={!!shop.active} onChange={() => toggleField(shop, "active")} color="success" /></TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(shop)}><EditIcon /></IconButton>
                      <IconButton onClick={() => handleDelete(shop)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {formOpen && (
          <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>{selectedShop ? "Edit Shop" : "Add Shop"}</DialogTitle>
            <ShopInfo shop={selectedShop} onShopUpdate={handleFormSaved} />
          </Dialog>
        )}

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast({ ...toast, open: false })}
        >
          <Alert severity={toast.severity} sx={{ width: "100%" }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </ProtectedRoute>
  );
};

export default ShopsPage;
